import type { AppStore } from '../useAppStore.ts'

export type TabId = 'runtime' | 'scenarios' | 'packBuilder'
export type ThemeOption = 'light' | 'dark' | 'system'
export type UserTier = 'free' | 'pro'

export interface UISlice {
  activeTab: TabId
  theme: ThemeOption
  resolvedTheme: 'light' | 'dark'
  inputPanelCollapsed: boolean
  showFormulaExplanation: boolean
  showAssumptionsSummary: boolean
  showAdvancedMode: boolean
  quickModeActive: boolean
  userTier: UserTier
  showLoadDemo: boolean
  setActiveTab: (tab: TabId) => void
  setTheme: (theme: ThemeOption) => void
  toggleInputPanel: () => void
  toggleFormulaExplanation: () => void
  toggleAssumptionsSummary: () => void
  toggleAdvancedMode: () => void
  toggleQuickMode: () => void
  setShowLoadDemo: (show: boolean) => void
}

function resolveTheme(theme: ThemeOption): 'light' | 'dark' {
  if (theme !== 'system') return theme
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

type Set = (fn: (state: AppStore) => Partial<AppStore>) => void

export function createUISlice(set: Set): UISlice {
  return {
    activeTab: 'runtime',
    theme: 'system',
    resolvedTheme: resolveTheme('system'),
    inputPanelCollapsed: false,
    showFormulaExplanation: false,
    showAssumptionsSummary: false,
    showAdvancedMode: false,
    quickModeActive: false,
    userTier: 'free',
    showLoadDemo: true,

    setActiveTab: (tab) => set(() => ({ activeTab: tab })),

    setTheme: (theme) => {
      const resolvedTheme = resolveTheme(theme)
      if (typeof document !== 'undefined') {
        document.documentElement.dataset.theme = resolvedTheme
      }
      set(() => ({ theme, resolvedTheme }))
    },

    toggleInputPanel: () => set((state) => ({ inputPanelCollapsed: !state.inputPanelCollapsed })),
    toggleFormulaExplanation: () => set((state) => ({ showFormulaExplanation: !state.showFormulaExplanation })),
    toggleAssumptionsSummary: () => set((state) => ({ showAssumptionsSummary: !state.showAssumptionsSummary })),
    toggleAdvancedMode: () => set((state) => ({ showAdvancedMode: !state.showAdvancedMode })),
    toggleQuickMode: () => set((state) => ({ quickModeActive: !state.quickModeActive })),
    setShowLoadDemo: (show) => set(() => ({ showLoadDemo: show })),
  }
}
